import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { useToast } from '@contexts/ToastContext'
import { useCityContext } from '@contexts/LocationContext'
import { validateCPF } from '@utils/validators'
import { formatCPF, formatPhone, lookupCep, parseDate } from '@utils'
import { Button, Loading, Dropdown } from '@components'
import { DOCUMENT_TYPES, STATES_OPTIONS } from '@config/constants'
import { DISABLE_PASSPORT_REGISTRATION } from '@config/features'
import DatePicker, { registerLocale } from 'react-datepicker'
import DatePickerInput from '@components/DatePickerInput'
import 'react-datepicker/dist/react-datepicker.css'
import ptBR from 'date-fns/locale/pt-BR'
import './CompleteRegistration.css'

registerLocale('pt-BR', ptBR)



const CompleteRegistration = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, isRegistrationComplete, completeRegistration, loading } = useAuth()
  const { showToast } = useToast()
  const { city } = useCityContext()
  
  const [step, setStep] = useState(1) // 1 for personal, 2 for address
  const [formData, setFormData] = useState({
    // Personal fields
    documentType: user?.document_type || 'CPF', // Initialize with existing or default to CPF
    cpf: user?.main_document 
      ? (user.document_type === 'CPF' ? formatCPF(user.main_document) : user.main_document) 
      : '',
    birthDate: user?.birth_date ? parseDate(user.birth_date) : null,
    phone: user?.phone || '',
    // Address fields
    cep: user?.cep || '',
    street: '',
    number: '',
    complement: '',
    neighborhood: user?.neighborhood || '',
    city: user?.city || '',
    state: user?.state || '',
  })
  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loadingCep, setLoadingCep] = useState(false) // Renamed from cepLoading

  // Initialize address fields if user has an existing address string
  useEffect(() => {
    if (user?.address) {
      const parts = user.address.split(',');
      if (parts.length >= 2) {
        setFormData(prev => ({
          ...prev,
          street: parts[0].trim(),
          number: parts[1].trim(),
          complement: parts.length > 2 ? parts[2].trim() : '',
        }));
      }
    }
  }, [user?.address]);

  // Redireciona se não estiver autenticado ou se já completou
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/${city?.slug || ''}/login`, { replace: true })
    } else if (user) {
      // Determine initial step based on existing data
      const hasPersonalData = user.main_document && user.birth_date;
      const hasAddressData = user.cep && user.address && user.neighborhood && user.city && user.state;

      if (hasPersonalData && !hasAddressData) {
        setStep(2); // Move to address step if personal is complete but address is not
      } else {
        setStep(1); // Default to personal data if not fully complete or not started address
      }
    }
  }, [isAuthenticated, isRegistrationComplete, navigate, user])


  const handleChange = (e) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === 'cpf') {
      if (formData.documentType === 'CPF') {
        formattedValue = value.replace(/\D/g, '').slice(0, 11)
      } else {
        formattedValue = value; // No formatting for other document types
      }
    } else if (name === 'phone') {
      formattedValue = value.replace(/\D/g, '').slice(0, 11)
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }))
    
    // Clear error for the changed field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleDateChange = (date) => {
    console.log('handleDateChange received date:', date);
    setFormData(prev => ({ ...prev, birthDate: date }))
    console.log('handleDateChange - formData.birthDate after update:', date);
    if (formErrors.birthDate) {
      setFormErrors(prev => ({ ...prev, birthDate: null }))
    }
  }

  const handleCepBlur = async (e) => {
    const rawCep = e.target.value.replace(/\D/g, ''); // Remove non-digits
    
    setFormData(prev => ({ ...prev, cep: rawCep.replace(/^(\d{5})(\d)/, '$1-$2') })); // Format for display
    setFormErrors(prev => ({ ...prev, cep: null }));

    if (rawCep.length === 8) {
      setLoadingCep(true);
      // Clear previous address errors and fields before lookup
      setFormErrors(prev => ({ ...prev, street: null, neighborhood: null, city: null, state: null }));
      setFormData(prev => ({ ...prev, street: '', neighborhood: '', city: '', state: '' }));

      const addressData = await lookupCep(rawCep);
      setLoadingCep(false);

      if (addressData) {
        setFormData(prev => {
          const newFormData = {
            ...prev,
            street: addressData.street,
            neighborhood: addressData.neighborhood,
            city: addressData.city, // Use city from cep.js mapping
            state: addressData.state, // Use state from cep.js mapping
          };
          return newFormData;
        });
      } else {
        setFormErrors(prev => ({ ...prev, cep: 'CEP não encontrado ou inválido.' }));
      }
    } else if (rawCep.length > 0 && rawCep.length < 8) {
      setFormErrors(prev => ({ ...prev, cep: 'CEP incompleto.' }));
      setFormData(prev => ({ ...prev, street: '', neighborhood: '', city: '', state: '' }));
    } else {
      // Clear address fields if CEP is empty
      setFormData(prev => ({ ...prev, street: '', neighborhood: '', city: '', state: '' }));
    }
  };

  const validatePersonalForm = () => {
    const errors = {}
    
    console.log('validatePersonalForm - formData.birthDate:', formData.birthDate);
    if (formData.documentType === 'CPF') {
      if (!formData.cpf) {
        errors.cpf = 'CPF é obrigatório'
      } else if (!validateCPF(formData.cpf)) {
        errors.cpf = 'CPF inválido'
      }
    } else {
      if (!formData.cpf) { // Using formData.cpf for other document types too
        errors.cpf = 'Número do documento é obrigatório';
      }
    }
    
    if (!formData.birthDate) {
      errors.birthDate = 'Data de nascimento é obrigatória'
    } else {
      const birth = parseDate(formData.birthDate)
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const m = today.getMonth() - birth.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
          age--
      }
      if (age < 16) {
        errors.birthDate = 'Você deve ter pelo menos 16 anos'
      }
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateAddressForm = () => {
    const errors = {}

    if (!formData.cep || formData.cep.replace(/\D/g, '').length !== 8) {
      errors.cep = 'CEP é obrigatório e deve ter 8 dígitos.'
    }
    if (!formData.street) {
      errors.street = 'Rua é obrigatória.'
    }
    if (!formData.number) {
      errors.number = 'Número é obrigatório.'
    }
    if (!formData.neighborhood) {
      errors.neighborhood = 'Bairro é obrigatório.'
    }
    if (!formData.city) {
      errors.city = 'Cidade é obrigatória.'
    }
    if (!formData.state) {
      errors.state = 'Estado é obrigatório.'
    }

    setFormErrors(prev => ({ ...prev, ...errors }))
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === 1) {
      if (!validatePersonalForm()) return;

      setIsSubmitting(true);
      const personalData = {};

      const isCpfDocument = formData.documentType === 'CPF';
      // Only send mainDocument and birthDate if they are not already complete AND it's a CPF document
      // If it's not a CPF document, it should always be editable and sent.
      const isMainDocumentImmutable = isCpfDocument && user?.verification_status === "CPF_DONE";

      personalData.document_type = formData.documentType;

      if (!isMainDocumentImmutable) {
        personalData.main_document = isCpfDocument ? formData.cpf.replace(/\D/g, '') : formData.cpf;
        const birthDate = formData.birthDate;
        const year = birthDate.getFullYear();
        const month = String(birthDate.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const day = String(birthDate.getDate()).padStart(2, '0');
        personalData.birth_date = `${year}-${month}-${day}`; // yyyy-mm-dd
      }


      if (formData.phone) {
        personalData.phone = formData.phone.replace(/\D/g, '');
      }

      let result;
      if (Object.keys(personalData).length === 0) {
        result = { success: true }; // Simulate success if no data to send
      } else {
        result = await completeRegistration(personalData);
      }

      if (result.success) {
        setStep(2); // Advance to address step
      } else if (result.error) {
        console.error('Erro ao salvar dados pessoais:', result.error);
        showToast(result.error, 'error'); // Display error toast
        // Optionally display error to user
      }
      setIsSubmitting(false);

    } else if (step === 2) {
      if (!validateAddressForm()) return;

      setIsSubmitting(true);

      const fullAddress = `${formData.street}, ${formData.number}${formData.complement ? `, ${formData.complement}` : ''}, ${formData.neighborhood}, ${formData.city}, ${formData.state}`;
      const addressData = {
        cep: formData.cep.replace(/\D/g, ''),
        address: fullAddress,
      };

      const result = await completeRegistration(addressData);

      if (result.success) {
        navigate(`/${city?.slug || ''}`, { replace: true });
      } else if (result.error) {
        console.error('Erro ao salvar endereço:', result.error);
        showToast(result.error, 'error'); // Display error toast
        // Optionally display error to user
      }
      setIsSubmitting(false);
    }
  };

  const handleNextStep = () => {
    // This function is now effectively handled by handleSubmit when type="submit"
    // on the "Continuar" button of step 1.
    // We can simulate a form submission or just trigger handleSubmit directly for validation.
    handleSubmit(new Event('submit', { cancelable: true })); // Trigger handleSubmit for current step
  };

  const handlePrevStep = () => {
    setStep(1);
  };

  const handleSkip = () => {
    navigate(`/${city?.slug || ''}/`, { replace: true })
  }

  if (loading) {
    return (
      <div className="complete-registration complete-registration--loading">
        <Loading size="lg" text="Carregando..." />
      </div>
    )
  }

    return (
    <main className="complete-registration">
      {isRegistrationComplete ? (
        <div className="complete-registration__container">
          <div className="complete-registration__card complete-registration__card--message">
            <h2 className="complete-registration__title">Cadastro Completo!</h2>
            <p className="complete-registration__subtitle">
              Seu cadastro já foi finalizado.<br />
              Para finalizar, confirme o seu e-mail.<br />
              Vá na sua caixa de entrada e clique no botão de confirmação que enviamos para você.<br />
              Se não encontrar, verifique a caixa de spam ou lixo eletrônico.
            </p>
          </div>
        </div>
      ) : (
        <div className="complete-registration__container">
          <div className="complete-registration__content">
            <div className="complete-registration__header">
              <h1 className="complete-registration__title">Complete seu cadastro</h1>
              <p className="complete-registration__subtitle">
                {step === 1 
                  ? 'Precisamos de algumas informações para você poder comprar ingressos'
                  : 'Agora informe seu endereço para entrega'}
              </p>
            </div>

            {/* Progress */}
            <div className="complete-registration__progress">
              <div className={`complete-registration__progress-step ${step >= 1 ? 'active' : ''}`}>
                <span className="complete-registration__progress-number">1</span>
                <span className="complete-registration__progress-label">Dados pessoais</span>
              </div>
              <div className="complete-registration__progress-line"></div>
              <div className={`complete-registration__progress-step ${step >= 2 ? 'active' : ''}`}>
                <span className="complete-registration__progress-number">2</span>
                <span className="complete-registration__progress-label">Endereço</span>
              </div>
            </div>

            <div className="complete-registration__card">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Dados pessoais */}
                {step === 1 && (
                  <div className="complete-registration__step">
                    <Dropdown
                      label="Tipo de Documento"
                      id="documentType"
                      name="documentType"
                      value={formData.documentType}
                      onChange={handleChange}
                      options={DISABLE_PASSPORT_REGISTRATION && user?.document_type !== 'Passaporte' ? DOCUMENT_TYPES.filter(t => t.value !== 'Passaporte') : DOCUMENT_TYPES}
                      error={formErrors.documentType}
                      disabled={!!user?.document_type || DISABLE_PASSPORT_REGISTRATION}
                    />

                    <div className="complete-registration__field">
                      <label htmlFor="mainDocument">{formData.documentType === 'CPF' ? 'CPF' : 'Número do Documento'}</label>
                      <input
                        type="text"
                        id="mainDocument"
                        name="cpf" // Keep name as 'cpf' for consistent state management
                        value={formData.documentType === 'CPF' ? formatCPF(formData.cpf) : formData.cpf}
                        onChange={handleChange}
                        placeholder={formData.documentType === 'CPF' ? '000.000.000-00' : 'Número do Documento'}
                        className={formErrors.cpf ? 'input--error' : ''}
                        disabled={!!user?.main_document}
                        maxLength={formData.documentType === 'CPF' ? 14 : undefined} // CPF has 14 chars with mask
                      />
                      {formErrors.cpf && (
                        <span className="complete-registration__field-error">{formErrors.cpf}</span>
                      )}
                    </div>

                    <div className="complete-registration__field">
                      <label htmlFor="birthDate">Data de nascimento</label>
                      <DatePicker
                        id="birthDate"
                        name="birthDate"
                        selected={formData.birthDate}
                        onChange={handleDateChange}
                        dateFormat="dd/MM/yyyy"
                        locale="pt-BR"
                        placeholderText="dd/mm/aaaa"
                        className={formErrors.birthDate ? 'input--error' : ''}
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={80}
                        maxDate={new Date()}
                        peekNextMonth
                        showMonthDropdown
                        dropdownMode="select"
                                              shouldCloseOnSelect
                                              disabled={!!user?.birth_date}
                                              customInput={<DatePickerInput className={formErrors.birthDate ? 'input--error' : ''} />}
                                            />                      {formErrors.birthDate && (
                        <span className="complete-registration__field-error">{formErrors.birthDate}</span>
                      )}
                    </div>

                    <div className="complete-registration__field">
                      <label htmlFor="phone">Telefone (WhatsApp)</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formatPhone(formData.phone)}
                        onChange={handleChange}
                        placeholder="(00) 00000-0000"
                        className={formErrors.phone ? 'input--error' : ''}
                      />
                      {formErrors.phone && (
                        <span className="complete-registration__field-error">{formErrors.phone}</span>
                      )}
                    </div>

                    <div className="complete-registration__actions">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleSkip}
                      >
                        Pular por agora
                      </Button>
                      <Button
                        type="submit" // Changed to submit to trigger handleSubmit for step 1
                        variant="primary"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        // onClick={handleNextStep} // Removed, as handleSubmit will handle logic
                      >
                        Continuar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Endereço */}
                {step === 2 && (
                  <div className="complete-registration__step">
                    <div className="complete-registration__row">
                      <div className="complete-registration__field complete-registration__field--cep">
                        <label htmlFor="cep">CEP</label>
                        <input
                          type="text"
                          id="cep"
                          name="cep"
                          value={formData.cep}
                          onChange={handleChange} // Keep handleChange for general input, handleCepBlur for specific logic
                          onBlur={handleCepBlur}
                          placeholder="00000-000"
                          className={formErrors.cep ? 'input--error' : ''}
                          maxLength={9}
                        />
                        {loadingCep && <span className="complete-registration__loading-cep">Buscando...</span>}
                        {formErrors.cep && (
                          <span className="complete-registration__field-error">{formErrors.cep}</span>
                        )}
                      </div>
                    </div>

                    <div className="complete-registration__field">
                      <label htmlFor="street">Rua</label>
                      <input
                        type="text"
                        id="street"
                        name="street"
                        value={formData.street}
                        onChange={handleChange}
                        placeholder="Nome da rua"
                        className={formErrors.street ? 'input--error' : ''}
                        disabled={isRegistrationComplete || loadingCep || formData.cep.replace(/\D/g, '').length !== 8}
                      />
                      {formErrors.street && (
                        <span className="complete-registration__field-error">{formErrors.street}</span>
                      )}
                    </div>

                    <div className="complete-registration__row">
                      <div className="complete-registration__field complete-registration__field--number">
                        <label htmlFor="number">Número</label>
                        <input
                          type="text"
                          id="number"
                          name="number"
                          value={formData.number}
                          onChange={handleChange}
                          placeholder="123"
                          className={formErrors.number ? 'input--error' : ''}
                        />
                        {formErrors.number && (
                          <span className="complete-registration__field-error">{formErrors.number}</span>
                        )}
                      </div>

                      <div className="complete-registration__field">
                        <label htmlFor="complement">Complemento</label>
                        <input
                          type="text"
                          id="complement"
                          name="complement"
                          value={formData.complement}
                          onChange={handleChange}
                          placeholder="Apto, bloco, etc."
                        />
                      </div>
                    </div>

                    <div className="complete-registration__field">
                      <label htmlFor="neighborhood">Bairro</label>
                      <input
                        type="text"
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleChange}
                        placeholder="Nome do bairro"
                        className={formErrors.neighborhood ? 'input--error' : ''}
                        disabled={isRegistrationComplete || loadingCep || formData.cep.replace(/\D/g, '').length !== 8}
                      />
                      {formErrors.neighborhood && (
                        <span className="complete-registration__field-error">{formErrors.neighborhood}</span>
                      )}
                    </div>

                    <div className="complete-registration__row">
                      <div className="complete-registration__field">
                        <label htmlFor="city">Cidade</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Nome da cidade"
                          className={formErrors.city ? 'input--error' : ''}
                          disabled={isRegistrationComplete || loadingCep || formData.cep.replace(/\D/g, '').length !== 8}
                        />
                        {formErrors.city && (
                          <span className="complete-registration__field-error">{formErrors.city}</span>
                        )}
                      </div>

                      <Dropdown
                        label="Estado"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        options={[{ value: "", label: "UF" }, ...STATES_OPTIONS]}
                        error={formErrors.state}
                        disabled={isRegistrationComplete || loadingCep || formData.cep.replace(/\D/g, '').length !== 8}
                      />
                    </div>

                    <div className="complete-registration__actions">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handlePrevStep}
                      >
                        Voltar
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={isSubmitting || loadingCep}
                        disabled={isSubmitting || loadingCep}
                      >
                        Finalizar cadastro
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </div>

            <p className="complete-registration__info">
              Você pode pular esta etapa, mas precisará completar o cadastro para comprar ingressos.
            </p>
          </div>
        </div>
      )}
    </main>
  )
}

export default CompleteRegistration